/** Prime the app store for a brand-new conversation, exactly as the Welcome page does. */

import { MessageRole } from '@/services/chat';
import { useAppStore } from '@/stores/useAppStore';
import type { MessageArtifact } from '@/stores/useAppStore';
import { buildStreamingMessage } from '@features/chat/hooks/messageStoreHelpers';
import { buildFullText } from '@features/chat/services/buildFullText';


import type { Attachment, ImageAttachment } from '@shared/components/attachments/types';

/**
 * Build message artifacts from image attachments for display.
 *
 * `origUrl` is a fresh object URL of the File, so the side panel shows full
 * resolution before the conversation reloads; it lives until the page unloads.
 */
function buildMessageArtifacts(images: ImageAttachment[]): MessageArtifact[] {
  return images.map(img => ({
    id: img.id,
    type: 'image' as const,
    thumbUrl: img.thumbBase64,
    origUrl: URL.createObjectURL(img.file),
    name: img.file.name,
  }));
}

/** Prepare the store state for a new conversation. */
export function prepareConversation(
  messageContent: string,
  conversationId: string,
  attachments: Attachment[] | undefined,
  artifactIds: string[] | undefined,
  imageConfig: Record<string, string | number> | undefined,
  /** What the composer had selected, when the caller is the chat page itself. */
  selection?: { modelId?: string | null | undefined; promptId?: string | undefined }
) {
  const store = useAppStore.getState();
  const imageAttachments = (attachments ?? []).filter(
    (a): a is ImageAttachment => a.type === 'image'
  );
  const hasImages = imageAttachments.length > 0;

  // Include pasted-text attachment content in the message body
  const fullText = buildFullText(messageContent, attachments);

  // Clear any existing messages to start fresh
  store.clearMessages();

  // Set the active conversation in the store so Chat.tsx can use it
  store.setActiveConversation({
    id: conversationId,
    title: messageContent.slice(0, 50) + (messageContent.length > 50 ? '...' : ''),
    isActive: true,
    isStarred: false,
    messages: [],
  });

  // Add user message to store for display (including artifacts for thumbnails)
  // Doubles as the send's client message id, so a Retry or Edit of this first
  // turn can name it to the server before a reload supplies its row id.
  const userMessageId = crypto.randomUUID();
  const messageArtifacts: MessageArtifact[] | undefined = hasImages
    ? buildMessageArtifacts(imageAttachments)
    : undefined;

  store.addMessage({
    id: userMessageId,
    role: MessageRole.USER,
    content: fullText,
    artifacts: messageArtifacts,
    sentAt: Date.now(),
  });

  // Create streaming message placeholder. Built by the shared helper so the
  // first turn carries `turnStartedAt` like every other turn.
  const streamingMessage = buildStreamingMessage();
  const streamingMessageId = streamingMessage.id;
  store.addMessage(streamingMessage);

  const clientMessageId = userMessageId;

  // Set pending message for Chat.tsx to send after mount (with pasted text included)
  store.setPendingMessage({
    content: fullText,
    conversationId,
    streamingMessageId,
    artifactIds,
    clientMessageId,
    imageConfig,
    ...(selection?.modelId !== undefined ? { modelId: selection.modelId } : {}),
    ...(selection?.promptId ? { promptId: selection.promptId } : {}),
  });
}

/**
 * Start a new conversation from plain text: mint the id, prime the store the
 * way the Welcome page does, and leave the send to the Chat page's
 * `useChatSetup`. No create call — the server creates the row on the first
 * message. Returns the conversation id.
 */
export function startConversation(text: string): string {
  const conversationId = crypto.randomUUID();
  prepareConversation(text.trim(), conversationId, undefined, undefined, undefined);
  return conversationId;
}
